import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Box, Button, Stack, TextField} from "@mui/material";
import {enqueueSnackbar} from "notistack";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FOLIO_NOTE_SX} from "../../system/billing/folio/editor/folioStyles";
import {DigitalSigningStatusView} from "../digitalSigning.model";
import SigningQrCode from "./SigningQrCode";
import SigningQrHandover from "./SigningQrHandover";
import {useSigningQrLogoUrl} from "./signingQrBranding.util";
import {TipWithoutServiceNotice} from "./DigitalSigningIssue";
import DigitalSigningStatusLine from "./DigitalSigningStatusLine";
import {useNow} from "./digitalSigningFlow.hooks";
import {
    copyText,
    countdownText,
    DIGITAL_SIGNING_BUTTON_SX,
    digitalSigningUrl,
    remainingMs
} from "./digitalSigningFlow.util";

interface Props {
    signing: DigitalSigningStatusView;
    token: string;
    busy: boolean;
    /** A tip would go on the folio without a service. */
    tipWithoutService?: boolean;
    onVoid: () => void;
    onExpired: () => void;
}

const QR_SIZE = 160;

const DigitalSigningLive = ({signing, token, busy, tipWithoutService, onVoid, onExpired}: Props) => {
    const {t} = useTranslation();
    const logoUrl = useSigningQrLogoUrl();
    const now = useNow();
    const [fullscreen, setFullscreen] = useState(false);
    const [showLink, setShowLink] = useState(false);
    const remaining = remainingMs(signing.expiresAt, now);
    const expired = remaining !== undefined && remaining <= 0;
    const url = digitalSigningUrl(window.location.origin, token);

    const expiryReportedRef = useRef(false);
    useEffect(() => {
        if (!expired || expiryReportedRef.current) return;
        expiryReportedRef.current = true;
        onExpired();
    }, [expired]);

    const copyLink = async () => {
        if (await copyText(url)) {
            enqueueSnackbar(t('Link copied to clipboard'), {variant: 'success'});
        } else {
            setShowLink(true);
            enqueueSnackbar(t('The link could not be copied.'), {variant: 'error'});
        }
    };

    return (
        <Stack gap={1.5}>
            <Box>
                <DigitalSigningStatusLine signing={signing}/>
                {remaining !== undefined && (
                    <Box sx={{...FOLIO_NOTE_SX, mt: 0.5, fontVariantNumeric: 'tabular-nums'}}>
                        {t('Expires in {{time}}', {time: countdownText(remaining)})}
                    </Box>
                )}
            </Box>

            {tipWithoutService && <TipWithoutServiceNotice templateId={signing.templateId}/>}

            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                <SigningQrCode value={url} size={QR_SIZE} logoUrl={logoUrl}/>
            </Box>

            <Stack gap={1}>
                <Button
                    variant={'contained'}
                    fullWidth
                    startIcon={<FullscreenIcon/>}
                    onClick={() => setFullscreen(true)}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Show fullscreen')}
                </Button>
                <Button
                    variant={'outlined'}
                    fullWidth
                    startIcon={<ContentCopyIcon/>}
                    onClick={copyLink}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Copy link')}
                </Button>
                <Button
                    variant={'outlined'}
                    fullWidth
                    startIcon={<OpenInNewIcon/>}
                    onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Open in new tab')}
                </Button>
                <Button
                    color={'error'}
                    fullWidth
                    startIcon={<LinkOffIcon/>}
                    onClick={onVoid}
                    disabled={busy}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Void link')}
                </Button>
            </Stack>

            {showLink && (
                <TextField
                    size={'small'}
                    fullWidth
                    value={url}
                    label={t('Link')}
                    helperText={t('Select the link and copy it')}
                    InputProps={{readOnly: true}}
                    onFocus={event => event.target.select()}
                    autoFocus
                />
            )}

            <SigningQrHandover open={fullscreen} onClose={() => setFullscreen(false)} value={url} logoUrl={logoUrl}/>
        </Stack>
    );
};

export default DigitalSigningLive;
